import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {useTranslation} from 'react-i18next';
import BolProduct from '../../../models/bolProductModel';
import User from '../../../models/userModel';
import {ActivateHandle} from '../../../util/interfaces';
import {HIDE_CONFIRM_DISMISSED_FLEX_KEY} from '../../../util/productsUtil';
import {createErrorMessage} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import HideProductDialog from './hideProductDialog';

interface Props {
    onHidden: (product: BolProduct) => void;
}

function HideProductDialogController(props: Props, ref: ForwardedRef<ActivateHandle<BolProduct>>) {
    const {onHidden} = props;
    const {userContext, onChangeContext} = useContext(AppContext);
    const {user} = userContext;
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [product, setProduct] = useState<BolProduct | null>(null);
    const [acknowledgedLists, setAcknowledgedLists] = useState(false);
    const [acknowledgedChannels, setAcknowledgedChannels] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    function onActivate(activated?: BolProduct) {
        if (!activated) return;
        setProduct(activated);
        setAcknowledgedLists(false);
        setAcknowledgedChannels(false);
        setDontShowAgain(false);
        setActive(true);
    }

    function onClose() {
        setActive(false);
    }

    function onConfirm() {
        if (!product || !acknowledgedLists || !acknowledgedChannels) return;
        setActive(false);
        if (dontShowAgain && user) dismissPermanently(user);
        hideProduct(product);
    }

    function dismissPermanently(user: User) {
        const updated = user.clone();
        updated.setFlex(HIDE_CONFIRM_DISMISSED_FLEX_KEY, true);
        updated.update(undefined, (saved: User, success: boolean) => {
            if (success) onChangeContext({user: saved});
        });
    }

    function hideProduct(product: BolProduct) {
        const hidden = product.clone();
        hidden.hiddenByUser = true;
        hidden.update(
            false,
            (updated: BolProduct, success: boolean, data: ResponseData) => {
                if (success) {
                    showSnackbar(t('products.hide.done', 'Het product is verborgen'));
                    onHidden(updated);
                } else if (!data.handled) showHideError(data.message);
            },
            () => showHideError()
        );
    }

    function showHideError(message?: string | null) {
        showDialog(
            createErrorMessage(message ?? t('products.hide.failed', 'Het verbergen van het product is niet gelukt.')),
            t('products.hide.failedTitle', 'Verbergen mislukt')
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate, close: onClose}));

    return (
        <HideProductDialog
            onClose={onClose}
            onConfirm={onConfirm}
            onCheckLists={setAcknowledgedLists}
            onCheckChannels={setAcknowledgedChannels}
            onCheckDontShowAgain={setDontShowAgain}
            active={active}
            product={product}
            acknowledgedLists={acknowledgedLists}
            acknowledgedChannels={acknowledgedChannels}
            dontShowAgain={dontShowAgain}
            canDismissPermanently={!!user}
        />
    );
}

export default forwardRef<ActivateHandle<BolProduct>, Props>(HideProductDialogController);
